<template>
  <div class="moderation-dashboard">
    <!-- Header -->
    <b-card class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2 class="mb-1">{{ $t('backoffice.moderation.title') || 'Moderation Dashboard' }}</h2>
          <p class="text-muted mb-0">
            {{ $t('backoffice.moderation.subtitle') || 'Review and moderate forum answers and comments pending approval' }}
          </p>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="warning" class="mr-2">
            {{ pendingAnswersCount }} {{ $t('backoffice.moderation.pending-answers') || 'Pending Answers' }}
          </b-badge>
          <b-badge variant="danger">
            {{ pendingCommentsCount }} {{ $t('backoffice.moderation.pending-comments') || 'Pending Comments' }}
          </b-badge>
        </div>
      </div>
    </b-card>

    <!-- Filters -->
    <b-card class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <b-form-select
            v-model="selectedContentType"
            :options="contentTypeOptions"
            class="mr-3"
            style="width: 200px"
          />
          <b-form-select
            v-model="selectedForum"
            :options="forumOptions"
            class="mr-3"
            style="width: 250px"
          />
        </div>
        <div>
          <b-button
            variant="outline-primary"
            @click="refreshData"
            :disabled="isLoading"
          >
            <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
            {{ $t('action.refresh') || 'Refresh' }}
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2">{{ $t('action.loading') || 'Loading...' }}</p>
    </div>

    <!-- Content Tabs -->
    <b-card v-else>
      <b-tabs content-class="mt-3">
        <!-- Forum Answers Tab -->
        <b-tab :title="$t('backoffice.moderation.forum-answers') || 'Forum Answers'" active>
          <div v-if="pendingAnswers.length === 0" class="text-center py-5">
            <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-3" />
            <h5>{{ $t('backoffice.moderation.no-pending-answers') || 'No Pending Answers' }}</h5>
            <p class="text-muted">{{ $t('backoffice.moderation.all-answers-moderated') || 'All forum answers have been moderated' }}</p>
          </div>

          <div v-else>
            <div
              v-for="answer in filteredPendingAnswers"
              :key="answer.key"
              class="moderation-item mb-3 p-3 border rounded"
            >
              <!-- Answer Header -->
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="32"
                    :src="getImageSrc(answer.ownedByMember && answer.ownedByMember.avatarURL)"
                    class="mr-2"
                  />
                  <div>
                    <strong>
                      {{ answer.ownedByMember && answer.ownedByMember.name }} {{ answer.ownedByMember && answer.ownedByMember.surname }}
                    </strong>
                    <div class="text-muted small">
                      {{ timestampToNow(answer.createdAt) }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-items-center">
                  <b-badge variant="warning" class="mr-2">
                    {{ $t('backoffice.moderation.pending') || 'Pending' }}
                  </b-badge>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="viewForum(answer.forumKey)"
                  >
                    {{ $t('backoffice.moderation.view-forum') || 'View Forum' }}
                  </b-button>
                </div>
              </div>

              <!-- Answer Content -->
              <div class="mb-3">
                <h6 class="mb-2">{{ $t('backoffice.moderation.answer-to') || 'Answer to' }}: {{ getForumName(answer.forumKey) }}</h6>
                <div v-html="translate(answer.description)" class="content-preview" />
              </div>

              <!-- Use existing ModerationSection component -->
              <moderation-section
                :content="answer"
                morph-type="forums"
                item-type="forumAnswers"
                item-type-name="answer"
                :parent-key="answer.forumKey"
                @moderation-complete="handleModerationComplete"
              />
            </div>
          </div>
        </b-tab>

        <!-- Comments Tab -->
        <b-tab :title="$t('backoffice.moderation.comments') || 'Comments'">
          <div v-if="pendingComments.length === 0" class="text-center py-5">
            <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-3" />
            <h5>{{ $t('backoffice.moderation.no-pending-comments') || 'No Pending Comments' }}</h5>
            <p class="text-muted">{{ $t('backoffice.moderation.all-comments-moderated') || 'All comments have been moderated' }}</p>
          </div>

          <div v-else>
            <div
              v-for="comment in filteredPendingComments"
              :key="comment.key"
              class="moderation-item mb-3 p-3 border rounded"
            >
              <!-- Comment Header -->
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="32"
                    :src="getImageSrc(comment.ownedByMember && comment.ownedByMember.avatarURL)"
                    class="mr-2"
                  />
                  <div>
                    <strong>
                      {{ comment.ownedByMember && comment.ownedByMember.name }} {{ comment.ownedByMember && comment.ownedByMember.surname }}
                    </strong>
                    <div class="text-muted small">
                      {{ timestampToNow(comment.createdAt) }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-items-center">
                  <b-badge variant="warning" class="mr-2">
                    {{ $t('backoffice.moderation.pending') || 'Pending' }}
                  </b-badge>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="viewCommentSource(comment)"
                  >
                    {{ $t('backoffice.moderation.view-source') || 'View Source' }}
                  </b-button>
                </div>
              </div>

              <!-- Comment Content -->
              <div class="mb-3">
                <h6 class="mb-2">{{ $t('backoffice.moderation.comment-on') || 'Comment on' }}: {{ getCommentSourceInfo(comment) }}</h6>
                <div v-html="comment.content" class="content-preview" />
              </div>

              <!-- Use existing ModerationSection component -->
              <moderation-section
                :content="comment"
                :morph-type="comment.morphType || 'comment'"
                item-type="comments"
                item-type-name="comment"
                :parent-key="getCommentParentKey(comment)"
                @moderation-complete="handleModerationComplete"
              />
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';
import { getImageResource } from '@/@core/utils/image-utils';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';
import ModerationSection from '@/@core/components/moderation/ModerationSection.vue';

export default {
  name: 'ModerationDashboard',
  
  components: {
    ModerationSection
  },

  mixins: [ToastNotificationsMixin],

  data() {
    return {
      isLoading: true,
      selectedContentType: 'all',
      selectedForum: 'all',
      pendingAnswers: [],
      pendingComments: [],
      forums: []
    };
  },

  computed: {
    collective() {
      return this.$store.state.collective.collective;
    },
    isStaff() {
      return this.collective.isStaff;
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    pendingAnswersCount() {
      return this.pendingAnswers.length;
    },
    pendingCommentsCount() {
      return this.pendingComments.length;
    },
    contentTypeOptions() {
      return [
        { value: 'all', text: this.$t('backoffice.moderation.all-content') || 'All Content' },
        { value: 'answers', text: this.$t('backoffice.moderation.forum-answers') || 'Forum Answers' },
        { value: 'comments', text: this.$t('backoffice.moderation.comments') || 'Comments' }
      ];
    },
    forumOptions() {
      const options = [
        { value: 'all', text: this.$t('backoffice.moderation.all-forums') || 'All Forums' }
      ];
      
      this.forums.forEach(forum => {
        options.push({
          value: forum.key,
          text: this.translate(forum.name)
        });
      });
      
      return options;
    },
    filteredPendingAnswers() {
      let answers = this.pendingAnswers;
      
      if (this.selectedForum !== 'all') {
        answers = answers.filter(answer => answer.forumKey === this.selectedForum);
      }
      
      return answers;
    },
    filteredPendingComments() {
      let comments = this.pendingComments;
      
      if (this.selectedForum !== 'all') {
        comments = comments.filter(comment => {
          // Try to find the forum key from the comment's context
          if (comment.morphType === 'answer') {
            // Find the answer this comment belongs to
            const answer = this.pendingAnswers.find(a => a.key === comment.key);
            return answer && answer.forumKey === this.selectedForum;
          }
          return true;
        });
      }
      
      return comments;
    }
  },

  async created() {
    if (!this.isStaff) {
      this.$router.push({ name: 'backoffice' });
      return;
    }
    
    await this.fetchData();
    this.updateBreadcrumbs();
  },

  methods: {
    getImageSrc(url) {
      return getImageResource(url);
    },
    translate(field) {
      return translateTranslationTable(this.currentLocale, field);
    },
    timestampToNow(timestamp) {
      moment.locale(this.currentLocale);
      return moment(timestamp).fromNow();
    },
    getForumName(forumKey) {
      const forum = this.forums.find(f => f.key === forumKey);
      return forum ? this.translate(forum.name) : (this.$t('backoffice.moderation.unknown-forum') || 'Unknown Forum');
    },
    getCommentSourceInfo(comment) {
      if (comment.morphType === 'answer') {
        const answer = this.pendingAnswers.find(a => a.key === comment.key);
        if (answer) {
          return `${this.$t('backoffice.moderation.answer-to') || 'Answer to'} "${this.getForumName(answer.forumKey)}"`;
        }
      }
      return this.$t('backoffice.moderation.unknown-source') || 'Unknown source';
    },
    getCommentParentKey(comment) {
      // For comments on answers, use the answer key as parent
      if (comment.morphType === 'answer') {
        return comment.key;
      }
      // For other comment types, use the source key
      return comment.sourceKey || comment.key;
    },
    async fetchData() {
      this.isLoading = true;
      
      try {
        // Fetch forums
        await this.$store.dispatch('getItems', {
          itemType: 'forums',
          storedKey: 'moderation-forums',
          forceAPICall: true,
          page: 1,
          perPage: 100
        });
        
        // Fetch pending forum answers
        await this.$store.dispatch('getItems', {
          itemType: 'forumAnswers',
          storedKey: 'moderation-answers',
          forceAPICall: true,
          page: 1,
          perPage: 100,
          requestConfig: {
            isDraft: true,
            moderationStatus: 0
          }
        });
        
        // Fetch pending comments
        await this.$store.dispatch('getItems', {
          itemType: 'comments',
          storedKey: 'moderation-comments',
          forceAPICall: true,
          page: 1,
          perPage: 100,
          requestConfig: {
            isDraft: true,
            moderationStatus: 0
          }
        });
        
        // Update local data
        this.updateLocalData();
        
      } catch (error) {
        console.error('Error fetching moderation data:', error);
        this.notifyError(this.$t('error-message.general-error') || 'An error occurred');
      } finally {
        this.isLoading = false;
      }
    },
    updateLocalData() {
      // Get forums from store
      const forumsData = this.$store.getters.forums;
      this.forums = forumsData['moderation-forums'] && forumsData['moderation-forums'].unpaginated || [];
      
      // Get pending answers from store
      const answersData = this.$store.getters.forumAnswers;
      this.pendingAnswers = answersData['moderation-answers'] && answersData['moderation-answers'].unpaginated || [];
      
      // Get pending comments from store
      const commentsData = this.$store.getters.comments;
      this.pendingComments = commentsData['moderation-comments'] && commentsData['moderation-comments'].unpaginated || [];
    },
    async refreshData() {
      await this.fetchData();
      this.notifySuccess(this.$t('backoffice.moderation.data-refreshed') || 'Moderation data refreshed successfully');
    },
    async handleModerationComplete({ action, contentKey, response }) {
      // Show success notification
      this.notifySuccess(
        this.$t(`forums.moderation.success-${action}`) || `Action ${action} completed successfully`
      );

      // Refresh data to update the list
      await this.fetchData();
    },
    viewForum(forumKey) {
      this.$router.push({ name: 'forum', params: { id: forumKey } });
    },
    viewCommentSource(comment) {
      // Navigate to the source of the comment
      if (comment.morphType === 'answer') {
        const answer = this.pendingAnswers.find(a => a.key === comment.key);
        if (answer) {
          this.viewForum(answer.forumKey);
        }
      }
    },
    updateBreadcrumbs() {
      const breadcrumbs = [
        {
          text: this.$t('backoffice.title') || 'Backoffice',
          to: { name: 'backoffice' }
        },
        {
          text: this.$t('backoffice.moderation.title') || 'Moderation Dashboard',
          active: true
        }
      ];
      this.$store.commit('app/SET_BREADCRUMBS', breadcrumbs);
    }
  }
};
</script>

<style scoped>
.moderation-dashboard {
  max-width: 1200px;
  margin: 0 auto;
}

.moderation-item {
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.moderation-item:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.content-preview {
  max-height: 200px;
  overflow: hidden;
  position: relative;
}

.content-preview::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(transparent, #f8f9fa);
}

.content-preview img {
  max-width: 100%;
  height: auto;
}
</style>
